import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { customerInfo as customerInfoShape, userShape } from 'constants/propTypes';
import { FormattedMessage } from 'react-intl';
import { useQuery, useApolloClient, useMutation } from '@apollo/client';
import { useHistory } from 'react-router';
import { ADJUST_POINTS_ENHANCEMENT, ADD_NOTE_TO_BOOKING, CLAIM_MISSING_POINTS } from 'store/mutations';
import { GET_LOYALTY_POINTS_STATEMENT, GET_BOOKING as GET_ALL_BOOKING_INFO, FETCH_SALESFORCE_TICKET_DATA } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { useManagedMutation } from 'utils/managedHooks';
import Modal from 'components/ui/Modal';
import { ActionButton } from 'components/ui/Button';
import AgentPortalError from 'errors/AgentPortalError';
import { ADJUST_POINTS_TYPE } from 'constants/clubEurostar';
import PointsStatementTable from './PointsStatementTable';
import PointsSummaryTable from './PointsSummaryTable';
import AdjustPointsForm from './AdjustPointsForm';
import { GET_BOOKING } from './graphql';
import { Error, SectionContainer, SectionTitleContainer, SectionTableContainer, ActionsList } from '../ClubEurostarMember.styles';
import { StyledButton } from '../../HomePage/HomePage.styles';
import { Column, Row } from '../../../components/shared/Layout';
import ClaimMissingPointsForm from './ClaimMissingPointsForm';

const compilePointsStatement = (data, customerInfo) => {
  const items = data?.profileLoyaltyPointsStatement?.items;
  if (!items) {
    return;
  }

  let statusPointsBalance = customerInfo.profile.loyalty.statusPoints;
  let rewardPointsBalance = customerInfo.profile.loyalty.rewardPoints;

  const pointsStatement = data.profileLoyaltyPointsStatement.items.map((statement) => {
    const completeStatement = {
      ...statement,
      statusPointsBalance: Number(statusPointsBalance),
      rewardPointsBalance: Number(rewardPointsBalance)
    };

    statusPointsBalance -= statement.statusPoints;
    rewardPointsBalance -= statement.rewardPoints;

    return completeStatement;
  });

  return pointsStatement;
};

const PointsStatement = ({ customerInfo, user, canPerformOperations, isLoungeStaffUser }) => {
  const { push } = useHistory();
  const client = useApolloClient();

  const [isPackage, setIsPackage] = useState(false);
  const [showTcnNotCheckedInMessage, setShowTcnNotCheckedInMessage] = useState(false);
  const [isBookingNonExistent, setIsBookingNonExistent] = useState(false);

  const [isAdjustPointsPopupOpened, setAdjustPointsPopupOpened] = useState(false);
  const [isClaimMissingPointsPopupOpened, setClaimMissingPointsPopupOpened] = useState(false);
  const [adjustPointsType, setAdjustPointsType] = useState(ADJUST_POINTS_TYPE.ADD);
  const [adjustPointsError, setAdjustPointsError] = useState(false);
  const [claimMissingPointsError, setClaimMissingPointsError] = useState(null);
  const membershipNumber = customerInfo.profile?.loyalty?.membershipNumber;
  const pointsCommonVariables = {
    page: 1,
    size: 128
  };
  const variables = {
    customerId: customerInfo?.profile?.customerId,
    ...pointsCommonVariables
  };
  const { loading, data, error } = useQuery(GET_LOYALTY_POINTS_STATEMENT, {
    variables,
    fetchPolicy: 'network-only'
  });
  const [adjustPoints, { loading: adjustPointsLoading }] = useManagedMutation(ADJUST_POINTS_ENHANCEMENT, {
    refetchQueries: ['getCustomerDetails', 'profileLoyaltyPointsStatement']
  });

  const [addNote, { loading: addNoteLoading }] = useManagedMutation(ADD_NOTE_TO_BOOKING);
  const [claimMissingPoints, { loading: claimMissingPointsLoading }] = useMutation(CLAIM_MISSING_POINTS, {
    refetchQueries: ['getCustomerDetails', 'profileLoyaltyPointsStatement']
  });

  const pointsStatement = useMemo(() => compilePointsStatement(data, customerInfo), [data, customerInfo]);

  const onAdjustPoints = (type) => {
    setAdjustPointsType(type);
    setAdjustPointsPopupOpened(true);
  };

  const onAdjustPointsSubmit = async (formData) => {
    if (formData.pnr) {
      try {
        const { errors } = await client.query({
          query: GET_BOOKING,
          variables: {
            reference: formData.pnr
          }
        });
        if (errors && errors.length) {
          throw errors[0];
        }

        const comments = `${formData.pnr ? `Booking Reference: '${formData.pnr}'. ` : ''}${
          formData.tcn ? `TCN: '${formData.tcn}'. ` : ''
        }Manual points adjustment in Voyager by ${user.username}: ${formData.reasonText}`;

        const result = await adjustPoints({
          variables: {
            membershipNumber: customerInfo.profile.loyalty.membershipNumber,
            amountRewardPoints:
              adjustPointsType === ADJUST_POINTS_TYPE.ADD ? parseInt(formData.rewardPoints, 10) : -parseInt(formData.rewardPoints, 10),
            amountAcceleratorPoints:
              adjustPointsType === ADJUST_POINTS_TYPE.ADD
                ? parseInt(formData.acceleratorPoints, 10)
                : -parseInt(formData.acceleratorPoints, 10),
            amountStatusPoints:
              adjustPointsType === ADJUST_POINTS_TYPE.ADD ? parseInt(formData.statusPoints, 10) : -parseInt(formData.statusPoints, 10),
            reasonCode: formData.reasonCode,
            reasonText: comments,
            comments: comments,
            actionBy: user.username,
            pnr: formData.pnr,
            tcn: formData.tcn
          }
        });

        if (result?.data?.adjustPointsEnhancement && formData.pnr) {
          const hasTcn = formData.tcn !== null && formData.tcn !== undefined && formData.tcn !== '';
          await addNote({
            variables: {
              note: `Points manually ${
                adjustPointsType === ADJUST_POINTS_TYPE.ADD ? 'added to' : 'deducted from'
              } Club Eurostar account: ${membershipNumber}${hasTcn ? ` and TCN ${formData.tcn}` : ''}.
                     Reward points: ${formData.rewardPoints}, Accelerator points: ${formData.acceleratorPoints}, Status points: ${
                formData.statusPoints
              }.`,
              bookingReference: formData.pnr
            }
          });
        }
      } catch (err) {
        setAdjustPointsError(err);
      }
    }

    setAdjustPointsPopupOpened(false);
  };

  if (adjustPointsError) {
    throw new AgentPortalError(adjustPointsError);
  }

  const onClaimMissingPointsClicked = () => {
    setClaimMissingPointsError(null);
    setClaimMissingPointsPopupOpened(true);
  };

  const onClaimMissingPointsSubmit = async (formData) => {
    try {
      const sqillsResponse = await client.query({
        query: GET_ALL_BOOKING_INFO,
        variables: {
          reference: formData.pnr
        }
      });

      const isPackageValue = sqillsResponse.data.booking.isPackage;
      setIsPackage(isPackageValue);

      if (!isPackageValue) {
        const salesforceResponse = await client.query({
          query: FETCH_SALESFORCE_TICKET_DATA,
          variables: {
            bookingReference: formData.pnr,
            ticketNumbers: [formData.tcn]
          }
        });

        const isTcnCheckedIn = salesforceResponse.data.fetchSalesforceTicketData[0].isCheckedIn;
        setShowTcnNotCheckedInMessage(!isTcnCheckedIn);

        if (!isPackageValue && isTcnCheckedIn) {
          try {
            const result = await claimMissingPoints({
              variables: {
                membershipNumber: customerInfo.profile.loyalty.membershipNumber,
                bookingReference: formData.pnr,
                ticketReference: formData.tcn,
                validateName: !(formData.overrideName === 'yes'),
                actionBy: user.username
              }
            });

            if (result?.data?.claimMissingPoints.success) {
              let noteMessage;
              if (formData.overrideName === 'no') {
                noteMessage = `Missing rewards points manually claimed for Club Eurostar account:
                  ${customerInfo.profile.loyalty.membershipNumber} and TCN: ${formData.tcn}`;
              } else {
                noteMessage = `Missing rewards points manually claimed for Club Eurostar account: 
                  ${customerInfo.profile.loyalty.membershipNumber} and TCN: ${formData.tcn}. Name validation waived`;
              }
              await addNote({
                variables: {
                  note: noteMessage,
                  bookingReference: formData.pnr
                }
              });
            }
            setClaimMissingPointsPopupOpened(false);
            setClaimMissingPointsError(null);
          } catch (err) {
            const graphQLErrors = AgentPortalError.flattenGraphQLErrors(err.graphQLErrors);
            setClaimMissingPointsError(graphQLErrors);
          }
        }
      }
    } catch (err) {
      setIsBookingNonExistent(formData.pnr);
    }
  };

  if (loading) {
    return (
      <SectionContainer>
        <Row span={16} height="10vh" data-testid="full-page-spinner">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <Modal
        isOpen={isAdjustPointsPopupOpened}
        title={
          <FormattedMessage id={adjustPointsType === ADJUST_POINTS_TYPE.ADD ? 'clubEurostar.addPoints' : 'clubEurostar.deductPoints'} />
        }
        onClose={() => setAdjustPointsPopupOpened(false)}
      >
        <AdjustPointsForm
          type={adjustPointsType}
          onSubmit={onAdjustPointsSubmit}
          onCancel={() => setAdjustPointsPopupOpened(false)}
          rewardPoints={customerInfo.profile.loyalty.rewardPoints}
          statusPoints={customerInfo.profile.loyalty.statusPoints}
          loading={adjustPointsLoading || addNoteLoading}
        />
      </Modal>
      <Modal
        isOpen={isClaimMissingPointsPopupOpened}
        title={<FormattedMessage id="clubEurostar.claimMissingPoints" />}
        onClose={() => {
          setClaimMissingPointsPopupOpened(false);
          setIsPackage(false);
          setShowTcnNotCheckedInMessage(false);
          setIsBookingNonExistent(false);
        }}
      >
        <ClaimMissingPointsForm
          error={claimMissingPointsError}
          isPackage={isPackage}
          showTcnNotCheckedInMessage={showTcnNotCheckedInMessage}
          isBookingNonExistent={isBookingNonExistent}
          onSubmit={onClaimMissingPointsSubmit}
          onCancel={() => {
            setClaimMissingPointsPopupOpened(false);
            setIsPackage(false);
            setShowTcnNotCheckedInMessage(false);
            setIsBookingNonExistent(false);
          }}
          loading={claimMissingPointsLoading || addNoteLoading}
        />
      </Modal>
      <ActionsList>
        <StyledButton
          onClick={() => push(`/search?membershipNumber=${membershipNumber}`)}
          data-testid="book-without-sub-button"
          disabled={!canPerformOperations}
          showTip={!canPerformOperations}
          textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
        >
          <FormattedMessage id="clubEurostar.bookWithoutSub" />
        </StyledButton>
        {!user.isStation && (
          <>
            <ActionButton
              type="button"
              data-cy="add-points-button"
              data-testid="add-points-button"
              onClick={() => onAdjustPoints(ADJUST_POINTS_TYPE.ADD)}
              disabled={!canPerformOperations}
              showTip={!canPerformOperations}
              textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
            >
              <FormattedMessage id="clubEurostar.addPoints" />
            </ActionButton>
            <ActionButton
              type="button"
              data-cy="deduct-points-button"
              data-testid="deduct-points-button"
              onClick={() => onAdjustPoints(ADJUST_POINTS_TYPE.DEDUCT)}
              disabled={!canPerformOperations || customerInfo.profile.loyalty.rewardPoints <= 0}
              showTip={!canPerformOperations}
              textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
            >
              <FormattedMessage id="clubEurostar.deductPoints" />
            </ActionButton>
            <ActionButton
              type="button"
              data-cy="claim-missing-points-button"
              data-testid="claim-missing-points-button"
              onClick={() => onClaimMissingPointsClicked()}
              disabled={!canPerformOperations}
              showTip={!canPerformOperations}
              textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
            >
              <FormattedMessage id="clubEurostar.claimMissingPoints" />
            </ActionButton>
          </>
        )}
      </ActionsList>
      <SectionTitleContainer>
        <FormattedMessage id="clubEurostar.pointsSummary" tagName="h3" />
      </SectionTitleContainer>
      <SectionTableContainer>
        <PointsSummaryTable customerInfo={customerInfo} />
      </SectionTableContainer>
      <SectionTitleContainer>
        <FormattedMessage id="clubEurostar.pointsStatement" tagName="h3" />
      </SectionTitleContainer>

      <SectionTableContainer>
        {error && (
          <Error>
            <FormattedMessage id="clubEurostar.defaultError" />
          </Error>
        )}
        {pointsStatement &&
          (!pointsStatement.length ? (
            <Error>
              <FormattedMessage id="clubEurostar.pointsStatementEmpty" />
            </Error>
          ) : (
            <PointsStatementTable pointsStatement={pointsStatement} customerInfo={customerInfo} />
          ))}
      </SectionTableContainer>
    </SectionContainer>
  );
};

PointsStatement.propTypes = {
  customerInfo: customerInfoShape,
  user: userShape,
  canPerformOperations: PropTypes.bool,
  isLoungeStaffUser: PropTypes.bool
};

export default PointsStatement;
